<template>
  <div class="satellites-list">
    <div class="satellites-header">
      <div class="satellites-header__left">
        <h2 class="satellites-header__title">{{ $t('satellites.title') }}</h2>
        <span class="satellites-header__count" v-if="items">{{ items.length }}</span>
      </div>
      <div class="satellites-header__right">
        <div class="satellites-search">
          <i class="fa fa-search satellites-search__icon"></i>
          <input
            type="text"
            class="satellites-search__input"
            v-model="search"
            :placeholder="$t('search')"
          />
        </div>
        <button class="satellites-btn satellites-btn--create" @click="$router.push('/admin/developer/satellites/create')">
          <i class="fa fa-plus"></i>
          <span>{{ $t('satellites.create') }}</span>
        </button>
      </div>
    </div>

    <div class="satellites-grid" v-if="!items">
      <div class="satellites-card satellites-card--skeleton" v-for="n in 4" :key="n">
        <div class="satellites-card__body">
          <div class="skel skel--title"></div>
          <div class="skel skel--line"></div>
          <div class="skel skel--line skel--short"></div>
        </div>
      </div>
    </div>

    <div class="satellites-empty" v-else-if="items.length === 0">
      <div class="satellites-empty__icon">
        <img :src="satelliteIcon" alt="" />
      </div>
      <p class="satellites-empty__text">{{ $t('satellites.emptyDescription') }}</p>
      <button class="satellites-btn satellites-btn--create" @click="$router.push('/admin/developer/satellites/create')">
        <i class="fa fa-plus"></i>
        <span>{{ $t('satellites.create') }}</span>
      </button>
    </div>

    <div class="satellites-grid" v-else>
      <div
        class="satellites-card"
        v-for="item in filteredItems"
        :key="item.uuid || item.id"
        @click="$router.push(`/admin/developer/satellites/${item.id}`)"
      >
        <div class="satellites-card__header">
          <div class="satellites-card__icon">
            <img :src="satelliteIcon" alt="" />
          </div>
          <span class="satellites-card__type">{{ item.auth_type || 'auth' }}</span>
        </div>

        <div class="satellites-card__body">
          <h3 class="satellites-card__name">{{ labelText(item.label, item.id) }}</h3>
          <code class="satellites-card__id">{{ item.id }}</code>

          <div class="satellites-card__versions">
            <div class="satellites-card__version">
              <span class="satellites-card__version-label">Dev</span>
              <span class="satellites-card__version-value" :class="{ 'is-active': item.dev_version }">
                {{ versionLabel(item, 'dev') }}
              </span>
            </div>
            <div class="satellites-card__version">
              <span class="satellites-card__version-label">Live</span>
              <span class="satellites-card__version-value" :class="{ 'is-live': item.live_version }">
                {{ versionLabel(item, 'live') }}
              </span>
            </div>
          </div>
        </div>

        <div class="satellites-card__footer">
          <b-badge :variant="statusVariant(item.status)" pill>{{ item.status || 'draft' }}</b-badge>
          <b-badge :variant="healthVariant(item.health_status)" pill>
            {{ $t('satellites.health') }}: {{ item.health_status || $t('unknown') }}
          </b-badge>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { healthVariant, labelText, statusVariant } from './helpers'
import satelliteIcon from '@/assets/satellite.svg'

export default {
  name: 'DeveloperSatellitesList',
  data() {
    return {
      items: null,
      satelliteIcon,
      search: ''
    }
  },
  computed: {
    filteredItems() {
      if (!this.items) return []
      if (!this.search.trim()) return this.items

      const q = this.search.toLowerCase().trim()
      return this.items.filter((item) => {
        const label = labelText(item.label, item.id).toLowerCase()
        return label.includes(q) || item.id.toLowerCase().includes(q)
      })
    }
  },
  methods: {
    healthVariant,
    labelText,
    statusVariant,
    versionLabel(item, env) {
      const version = env === 'dev' ? item.dev_version : item.live_version
      if (!version) return env === 'dev' ? this.$t('notActive') : this.$t('notPublished')

      const schemaVersion = env === 'dev' ? item.dev_schema_version : item.live_schema_version
      if (schemaVersion) return schemaVersion
      if (typeof version === 'string' && !/^\d+$/.test(version)) return version
      return `v${version}`
    },
    hydrateVersionLabels(items) {
      return Promise.all(items.map((item) => {
        return this.$integrationApi
          .get(`/satellites/templates/${item.id}/versions`)
          .then((resp) => {
            const versions = resp.data || []
            const dev = versions.find((version) => version.version === Number(item.dev_version) || version.schema_version === item.dev_version)
            const live = versions.find((version) => version.version === Number(item.live_version) || version.schema_version === item.live_version)
            return {
              ...item,
              dev_schema_version: dev && dev.schema_version ? dev.schema_version : '',
              live_schema_version: live && live.schema_version ? live.schema_version : ''
            }
          })
          .catch(() => item)
      }))
    },
    load() {
      this.$integrationApi
        .get('/satellites/templates')
        .then((resp) => {
          return this.hydrateVersionLabels(resp.data || [])
        })
        .then((items) => {
          this.items = items
        })
        .catch((err) => {
          this.$debugError(err)
          this.$bvToast.toast(this.$t('satellites.failedToLoad'), {
            title: this.$t('error'),
            variant: 'danger'
          })
          this.items = []
        })
    }
  },
  mounted() {
    this.load()
  }
}
</script>

<style scoped lang="scss">
.satellites-list {
  padding: 8px 0;
}

.satellites-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 28px;
}

.satellites-header__left {
  display: flex;
  align-items: baseline;
  gap: 12px;
}

.satellites-header__right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.satellites-header__title {
  font-size: 22px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
}

.satellites-header__count {
  font-size: 13px;
  color: var(--text-muted);
  font-weight: 500;
}

.satellites-search {
  position: relative;
  display: flex;
  align-items: center;
}

.satellites-search__icon {
  position: absolute;
  left: 12px;
  font-size: 12px;
  color: var(--text-muted);
  pointer-events: none;
}

.satellites-search__input {
  width: 200px;
  height: 38px;
  padding: 0 14px 0 34px;
  background: var(--bg-card);
  border: 1px solid var(--border-default);
  border-radius: 8px;
  color: var(--text-primary);
  font-size: 13px;
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;

  &::placeholder { color: var(--text-muted); }

  &:focus {
    border-color: var(--primary-start);
    box-shadow: 0 0 0 3px rgba(91, 124, 246, 0.12);
  }
}

.satellites-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: none;
  outline: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  border-radius: 8px;
  transition: all 0.18s ease;

  &--create {
    padding: 9px 20px;
    background: var(--primary-start);
    color: #fff;

    &:hover {
      filter: brightness(1.06);
      transform: translateY(-1px);
    }
  }
}

.satellites-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 18px;
}

.satellites-card {
  background: var(--bg-card);
  border: 1px solid var(--border-default);
  border-radius: 8px;
  cursor: pointer;
  min-height: 210px;
  overflow: hidden;
  transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;

  &:hover {
    border-color: var(--primary-start);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
    transform: translateY(-2px);
  }
}

.satellites-card--skeleton {
  cursor: default;
}

.satellites-card__header,
.satellites-card__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
}

.satellites-card__icon {
  width: 38px;
  height: 38px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--primary-start);
  background: rgba(91, 124, 246, 0.12);
  font-size: 18px;

  img {
    width: 22px;
    height: 22px;
    display: block;
  }
}

.satellites-card__type {
  color: var(--text-muted);
  font-size: 12px;
  text-transform: uppercase;
}

.satellites-card__body {
  padding: 0 16px 14px;
}

.satellites-card__name {
  font-size: 17px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 4px;
}

.satellites-card__id {
  display: block;
  color: var(--text-muted);
  margin-bottom: 18px;
  word-break: break-word;
}

.satellites-card__versions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.satellites-card__version {
  border: 1px solid var(--border-default);
  border-radius: 8px;
  padding: 9px 10px;
}

.satellites-card__version-label {
  display: block;
  color: var(--text-muted);
  font-size: 11px;
  margin-bottom: 4px;
  text-transform: uppercase;
}

.satellites-card__version-value {
  display: block;
  color: var(--text-muted);
  font-size: 13px;
  font-weight: 600;
  line-height: 1.25;
  word-break: break-word;

  &.is-active { color: #45b8ff; }
  &.is-live { color: #2eb85c; }
}

.satellites-empty {
  text-align: center;
  padding: 72px 24px;
  background: var(--bg-card);
  border: 1px solid var(--border-default);
  border-radius: 8px;
}

.satellites-empty__icon {
  margin-bottom: 16px;

  img {
    width: 48px;
    height: 48px;
  }
}

.satellites-empty__text {
  color: var(--text-muted);
  margin-bottom: 18px;
}

.skel {
  background: rgba(255, 255, 255, 0.08);
  border-radius: 6px;
  height: 14px;
  margin-bottom: 12px;
}

.skel--title {
  height: 22px;
  width: 60%;
  margin-top: 44px;
}

.skel--line { width: 90%; }
.skel--short { width: 45%; }

@media (max-width: 767px) {
  .satellites-header,
  .satellites-header__right {
    align-items: stretch;
    flex-direction: column;
  }

  .satellites-search__input {
    width: 100%;
  }
}
</style>
